import { FORMAT_BYTES, type TexFormat } from './types.js';

/** Result of probing the browser for WebGPU support. */
export interface GpuSupport {
  supported: boolean;
  reason?: string;
}

/**
 * Cheap synchronous probe. Does not request an adapter, so it is safe to call
 * during first paint to decide whether to show the "WebGPU required" screen.
 */
export function probeWebGpu(): GpuSupport {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) {
    return {
      supported: false,
      reason:
        'This browser does not expose navigator.gpu. Use Chrome/Edge 121+ (desktop) or a browser with WebGPU enabled.',
    };
  }
  if (typeof globalThis.GPUDevice === 'undefined') {
    return { supported: false, reason: 'WebGPU types are missing from this browser build.' };
  }
  return { supported: true };
}

/** Everything the pipeline needs to know about the device it ended up with. */
export interface GpuCapabilities {
  /** `GPUAdapterInfo` when the browser exposes it. */
  readonly vendor: string;
  readonly architecture: string;
  readonly device: string;
  readonly description: string;
  /** Largest 2D texture edge; caps the resolution we can hold on the GPU. */
  readonly maxTextureDimension2D: number;
  readonly maxBufferSize: number;
  readonly maxStorageBufferBindingSize: number;
  readonly maxComputeInvocationsPerWorkgroup: number;
  readonly maxComputeWorkgroupSizeX: number;
  readonly maxComputeWorkgroupSizeY: number;
  /** `shader-f16` - lets ONNX Runtime run fp16 kernels natively. */
  readonly shaderF16: boolean;
  /** `timestamp-query` - enables the built-in pass profiler. */
  readonly timestampQuery: boolean;
  /** `subgroups` - ORT uses it for faster matmuls when present. */
  readonly subgroups: boolean;
  /** True when the adapter reports itself as a software rasteriser. */
  readonly isFallback: boolean;
}

export function readCapabilities(device: GPUDevice, isFallback: boolean): GpuCapabilities {
  const limits = device.limits;
  // `adapterInfo` is the modern accessor; older builds only had `adapter.info`.
  const info = (device as GPUDevice & { adapterInfo?: GPUAdapterInfo }).adapterInfo;
  return {
    vendor: info?.vendor ?? 'unknown',
    architecture: info?.architecture ?? 'unknown',
    device: info?.device ?? 'unknown',
    description: info?.description ?? '',
    maxTextureDimension2D: limits.maxTextureDimension2D,
    maxBufferSize: Number(limits.maxBufferSize),
    maxStorageBufferBindingSize: Number(limits.maxStorageBufferBindingSize),
    maxComputeInvocationsPerWorkgroup: limits.maxComputeInvocationsPerWorkgroup,
    maxComputeWorkgroupSizeX: limits.maxComputeWorkgroupSizeX,
    maxComputeWorkgroupSizeY: limits.maxComputeWorkgroupSizeY,
    shaderF16: device.features.has('shader-f16'),
    timestampQuery: device.features.has('timestamp-query'),
    subgroups: device.features.has('subgroups'),
    isFallback,
  };
}

/**
 * Largest square edge that fits `budgetBytes` of VRAM for `layers` simultaneous
 * textures of `format`, also respecting `maxTextureDimension2D`.
 *
 * The matting and upscaling stages both need a handful of full resolution
 * intermediates, so this is what decides when we have to fall back to tiling.
 */
export function maxWorkingEdge(
  caps: GpuCapabilities,
  format: TexFormat,
  layers: number,
  budgetBytes: number,
): number {
  const perPixel = FORMAT_BYTES[format] * Math.max(1, layers);
  const byBudget = Math.floor(Math.sqrt(budgetBytes / perPixel));
  return Math.max(64, Math.min(caps.maxTextureDimension2D, byBudget));
}
